// Shared types used by the frontend, the Netlify functions, and the pipeline.

/** Stable screen identifiers (also used as the `screen` value in `alternatives`). */
export type Screen =
  | 'fossil_fuel'
  | 'weapons'
  | 'tobacco'
  | 'gambling'
  | 'nuclear'
  | 'palm_oil'
  | 'animal_testing'
  | 'alcohol'
  | 'pesticides'

/** Boolean involvement flags, mirroring the `companies` columns (Leeway-sourced). */
export interface CompanyFlags {
  flag_fossil_fuel: boolean
  flag_weapons: boolean
  flag_tobacco: boolean
  flag_gambling: boolean
  flag_nuclear: boolean
  flag_palm_oil: boolean
  flag_pesticides: boolean
  flag_animal_testing: boolean
  flag_alcohol: boolean
  flag_military_contract: boolean
  flag_fur_leather: boolean
}

export interface Company extends CompanyFlags {
  isin: string
  ticker: string | null
  name: string
  sector: string | null
}

export interface Fund {
  isin: string
  ticker: string
  name: string
  domicile?: string
}

export interface Holding {
  fund_isin: string
  company_isin: string
  weight_pct: number // weight of the company WITHIN the fund, as a percentage (e.g. 1.14)
}

export interface Alternative {
  replaces_fund_isin: string
  alternative_fund_isin: string
  screen: Screen
  expense_ratio: number | null
  tracking_notes: string | null
  available_at: string[]
}

/** A single line of the user's portfolio: a fund ticker and its allocation %. */
export interface PortfolioEntry {
  ticker: string
  allocation_pct: number // share of the whole portfolio, as a percentage (e.g. 60)
}

/** One screen's aggregate exposure across the whole portfolio. */
export interface ScreenExposure {
  screen: Screen
  exposure_pct: number // 0–100, share of the portfolio exposed to this screen
}

/** A company driving exposure, aggregated across all funds in the portfolio. */
export interface OffendingHolding {
  company_isin: string
  name: string
  ticker: string | null
  sector: string | null
  contribution_pct: number // this company's total weighted share of the portfolio
  screens: Screen[] // active screens this company triggers
}

/** An alternative fund suggestion attached to a flagged fund the user holds. */
export interface AlternativeSuggestion extends Alternative {
  alternative_ticker: string | null
  alternative_name: string | null
  replaces_ticker: string | null
}

export interface AnalyseResult {
  exposures: ScreenExposure[]
  topOffenders: OffendingHolding[]
  alternatives: AlternativeSuggestion[]
  matchedTickers: string[]
  unmatchedTickers: string[]
  allocationSum: number // sum of supplied allocations; ~100 expected
}

export interface AnalyseRequest {
  portfolio: PortfolioEntry[]
  screens: Screen[]
}

/** Maps each UI screen to the company flag columns that satisfy it (OR semantics). */
export const SCREEN_FLAGS: Record<Screen, (keyof CompanyFlags)[]> = {
  fossil_fuel: ['flag_fossil_fuel'],
  weapons: ['flag_weapons', 'flag_military_contract'],
  tobacco: ['flag_tobacco'],
  gambling: ['flag_gambling'],
  nuclear: ['flag_nuclear'],
  palm_oil: ['flag_palm_oil'],
  animal_testing: ['flag_animal_testing'],
  alcohol: ['flag_alcohol'],
  pesticides: ['flag_pesticides'],
}

/** Human-readable labels for the UI. */
export const SCREEN_LABELS: Record<Screen, string> = {
  fossil_fuel: 'Fossil fuels',
  weapons: 'Weapons & defence',
  tobacco: 'Tobacco',
  gambling: 'Gambling',
  nuclear: 'Nuclear energy',
  palm_oil: 'Palm oil',
  animal_testing: 'Animal testing',
  alcohol: 'Alcohol',
  pesticides: 'Pesticides',
}

export const ALL_SCREENS: Screen[] = Object.keys(SCREEN_LABELS) as Screen[]

/** Screens pre-selected on first load (per spec). */
export const DEFAULT_SCREENS: Screen[] = ['fossil_fuel', 'weapons', 'tobacco', 'gambling']
